// next-app/src/components/survey/check-in-survey.tsx
'use client';

import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSurveyConfig, useSubmitSurvey, useSubmitVehiclesOfInterest } from '@/hooks';
import { SurveyForm, PersonInfo, VehicleSelector, Waiver, EmailOptIn } from '@/components/survey';
import { checkInSurveySchema, type CheckInSurveyForm } from '@/lib/validations/survey';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';

interface CheckInSurveyProps {
  hash: string;
  deviceSurveyGuid?: string;
}

export function CheckInSurvey({ hash, deviceSurveyGuid }: CheckInSurveyProps) {
  const router = useRouter();

  // Build redirect paths - check-in may have a device survey guid
  const successPath = '/submit/success';
  const errorPath = '/submit/error';

  // Fetch survey configuration
  const { data: configData, isLoading, isError, error } = useSurveyConfig(hash);
  const config = configData?.config;

  // Form setup - check-in requires waiver
  const form = useForm<CheckInSurveyForm>({
    resolver: zodResolver(checkInSurveySchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      event_id: 0,
      hash: hash,
      survey_type: 'check-in',
      responses: {},
      waiver_accepted: false,
      email_opt_in: false,
    },
  });

  // Update form when config loads
  useEffect(() => {
    if (config) {
      form.setValue('event_id', config.event_id);
    }
  }, [config, form]);

  // Submission handlers
  const submitSurvey = useSubmitSurvey(hash, {
    onSuccess: (participantId) => {
      if (participantId && selectedVehicles.length > 0) {
        submitVehiclesOfInterest.mutate({
          participant_id: participantId,
          vehicle_ids: selectedVehicles,
        });
      }
      router.push(successPath);
    },
    onError: () => {
      router.push(errorPath);
    },
  });

  const submitVehiclesOfInterest = useSubmitVehiclesOfInterest(hash);

  // Track selected vehicles
  const selectedVehicles =
    (form.watch('responses.vehicles_interested') as number[] | undefined) ?? [];

  const handleSubmit = async (data: CheckInSurveyForm) => {
    submitSurvey.mutate({
      ...data,
      responses: {
        ...data.responses,
        vehicles_interested: selectedVehicles,
      },
    });
  };

  if (isLoading) {
    return (
      <div className="mx-auto max-w-2xl space-y-6">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (isError || !config) {
    return (
      <div className="mx-auto max-w-2xl">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            {error?.message || 'Unable to load survey. Please check the URL and try again.'}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <SurveyForm
      form={form}
      onSubmit={handleSubmit}
      title="Event Check-In"
      description="Please complete this form to check in to the event"
      eventName={config.event_name}
      dealerName={config.dealer_name}
      isSubmitting={submitSurvey.isPending}
      submitText="Check In"
    >
      <PersonInfo disabled={submitSurvey.isPending} />

      {config.vehicles && config.vehicles.length > 0 && (
        <>
          <hr className="my-6" />
          <VehicleSelector
            vehicles={config.vehicles}
            name="responses.vehicles_interested"
            title="Which vehicles are you interested in?"
            description="Select any vehicles you would like to learn more about"
            disabled={submitSurvey.isPending}
          />
        </>
      )}

      <hr className="my-6" />

      <Waiver waiverText={config.waiver_text} disabled={submitSurvey.isPending} />

      <hr className="my-6" />

      <EmailOptIn optInText={config.email_opt_in_text} disabled={submitSurvey.isPending} />
    </SurveyForm>
  );
}
